import React, { memo, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useLocation } from "react-router-dom";

import { ErrorView } from "components/common/ErrorView/ErrorView";
import { ErrorType } from "components/common/ErrorView/types";
import { REACT_APP_STAGE } from "config/config";
import { INTEG, INTEG_GA_USER_COOKIE_NAME, PROD_GA_USER_COOKIE_NAME, PRODUCTION } from "config/constants";
import { User } from "types/users";

import { RouteSpec } from "./routeSpecs";
import { TermsOfServiceGate } from "./TermsOfServiceGate";


const createUserDataCookie = (name: string, value: string) => {
  // One year expiration
  const date = new Date();
  date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000);
  const expires = `; expires=${date.toUTCString()}`;
  // eslint-disable-next-line prefer-template, no-useless-concat
  const cookieName = `${name}=${value || ""}${expires}; path=/; domain=.groundlight.ai`;
  document.cookie = cookieName;
};

function ErrorFallback() {
  return <ErrorView error={ErrorType.ERROR_RENDERING} />;
}

interface CustomRoutesContainerProps {
  routeSpec: RouteSpec; // This is used to configure things within the RouteContainer
  setRouteSpec: (spec: RouteSpec) => void; // This hook is used to trigger changes outside the container
  user: User;
  children: React.ReactNode;
}



// TODO: This component could be improved and maybe merge to another component
/**
 * This route container is basically an intermediary between the overall website and a
 * specific route. This includes:
 * - setting route names in the header
 * - checking for terms of service
 */
export const CustomRoutesContainer = memo((props: CustomRoutesContainerProps) => {
  const { routeSpec, setRouteSpec, children, user } = props;

  const location = useLocation(); // What URL path are we currently at?

  useEffect(() => {
    if (user && user.id && [PRODUCTION, INTEG].includes(REACT_APP_STAGE)) {
      /*
      Google Analytics is only enabled in production and integ
      depending on the environment, we'll create or update a cookie, following this logic when the user is logged in:
      if exists: then update the cookie content only if the content is different.
      if does not exist: create with the content
      cookie content:
      - user_id
      - customer_type
      - customer_tier
      that cookie is coded using base64 and and it can be read by GTM across all the sites with the same domain

      NOTE:
      * cookie name for production is: gl_user
      * cookie name for integ is: integ_gl_user
      */
      //  Cookie content in base64
      const cookieContent = btoa(
        JSON.stringify({
          user_id: user?.id,
          customer_type: user?.userprofile?.customer_type,
          customer_tier: user?.tier,
        })
      );
      // Getting the cookie
      const expectedCookieName = PRODUCTION === REACT_APP_STAGE ? PROD_GA_USER_COOKIE_NAME : INTEG_GA_USER_COOKIE_NAME;
      const cookie = document.cookie.split(";").find((c) => c.trim().startsWith(`${expectedCookieName}=`));

      // We need to check if the content is different, this is to avoid updating the cookie every time
      if (cookie) {
        const cookieContentFromCookie = cookie.split(`${expectedCookieName}=`)[1];
        if (cookieContentFromCookie !== cookieContent) {
          createUserDataCookie(expectedCookieName, cookieContent);
        }
      } else {
        createUserDataCookie(expectedCookieName, cookieContent);
      }
    }
  }, [user]);

  // This hook lets us show the page name in the header! Cool!
  useEffect(() => {
    setRouteSpec(routeSpec);
  }, [setRouteSpec, routeSpec]);

  // Decide whether or not to put a terms-of-service gate on this route
  if (routeSpec.tos_exempt) {
    return <div>{children}</div>; // No TOS gate
  }

  return (
    // the key on the ErrorBoundary ensures that it updates when navigating to another page
    <TermsOfServiceGate user={user}>
      <ErrorBoundary key={location.key} FallbackComponent={ErrorFallback}>
        {children}
      </ErrorBoundary>
    </TermsOfServiceGate>
  ); // TOS gated
});
